/**
 * NotificationBell — in-app alert center.
 *
 * Sits in the top-right of the header (replacing the old theme toggle,
 * since Luna is dark-only). Shows:
 *
 *   1. A glass pill with a bell icon.
 *   2. An unread badge with the count of fresh alerts.
 *   3. A glass dropdown sheet that opens on tap and lists every alert
 *      the backend has sent the user, sourced from the same backend
 *      pipeline that powers the Telegram push messages — so the bell
 *      and the Telegram chat are always in sync.
 *
 * Alert kinds mapped here match the `kind` strings used by the
 * backend notificationService / cronNotificationService. When a new
 * kind is added on the backend, add a matching entry to the
 * KIND_REGISTRY below so the label/icon render correctly in the bell.
 *
 * Data flow:
 *   - GET  /api/luna/notifications      — list + unreadCount
 *   - POST /api/luna/notifications/read — mark-all-read (idempotent)
 *
 * While the user isn't authenticated yet (no JWT) the component shows
 * a static "empty" state — no errors, no spinners. As soon as the
 * auth flow (task #10) populates the token, the effect re-fetches.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Bell,
  Sparkles,
  Users,
  TrendingUp,
  Flame,
  Trophy,
  Award,
  Zap,
  Clock,
  Gift,
  ArrowDownCircle,
  ArrowUpCircle,
  AlertTriangle,
  Rocket,
  BarChart3,
  Gem,
  Package,
  CheckCircle,
  XCircle,
  UserPlus,
  CircleDollarSign,
} from 'lucide-react';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useUISounds } from '@/hooks/useUISounds';
import { lunaApi, getAuthToken } from '@/services/api';
import { cn } from '@/lib/cn';

// ────────────────────────────────────────────────────────────────────────────
// Kind registry — one entry per notification `kind` that the backend
// can emit. The bell reads this to decide the label, tone and icon
// for each row. Keys MUST match notificationService.ts kinds exactly.
// ────────────────────────────────────────────────────────────────────────────
type Tone = 'lunar' | 'plasma' | 'nebula' | 'positive' | 'warning' | 'negative';
type Lang = 'ru' | 'en';

// Богатая мета-нагрузка, которую бэкенд может присылать в колокольчик.
// Все поля опциональные — фронт рисует то, что есть, иначе фолбэк.
interface NotifDetails {
  amount?: string | number | null;
  price?: string | number | null;
  total?: string | number | null;
  max?: string | number | null;
  progress?: number | null;
  slot?: string | null;
  partner?: string | null;
  counterparty?: string | null;
  level?: number | null;
  line?: number | null;
  lineBonusPct?: string | null;
  bonusPct?: string | null;
  status?: string | null;
  rank?: string | null;
  count?: number | null;
  cost?: string | number | null;
  until?: string | null;
  lost?: string | number | null;
  requiredRefs?: number | null;
  currentRefs?: number | null;
  current?: number | null;
  daysLeft?: number | null;
  days?: number | null;
  fromCount?: number | null;
  remaining?: string | number | null;
  initial?: string | number | null;
  pct?: string | null;
  missing?: string | number | null;
  bonus?: string | number | null;
  slots?: Array<{ name: string; price: string }> | null;
  month?: number | null;
  // Daily earnings digest
  oneTime?: string | number | null;
  oneTimePayers?: number | null;
  daily?: string | number | null;
  dailyPayers?: number | null;
  // FOMO digest
  lostNoActive?: string | number | null;
  lostGate?: string | number | null;
  gateLevels?: number[] | null;
  // Deposit
  network?: string | null;
  tx_hash?: string | null;
  // Slot info enrichment — для рефералки и FOMO-уведомлений
  // (чтобы в колокольчике сразу было видно что за слот партнёр купил)
  slotName?: string | null;
  slotPrice?: string | number | null;
  // Цепочка апстрима: "Вы → @alice → @bob"
  chain?: string | null;
}

interface KindMeta {
  labelKey: string;
  description: (
    t: TFunction,
    lang: Lang,
    refId: string | null,
    meta?: NotifDetails | null,
  ) => string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  tone: Tone;
}

// Числовой формат: локаль подставляется от текущего языка UI.
function fmtAmount(raw: string | number | null | undefined, lang: Lang): string {
  if (raw === null || raw === undefined || raw === '') return '';
  const n = Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  const short = parseFloat(n.toFixed(3));
  return short.toLocaleString(lang === 'ru' ? 'ru-RU' : 'en-US', {
    maximumFractionDigits: 3,
  });
}

function partnerTag(name?: string | null): string {
  if (!name) return '';
  return name.startsWith('@') ? name : `@${name}`;
}

function buildSlotTag(
  slotName: string | null | undefined,
  slotPrice: string | number | null | undefined,
  lang: Lang,
): string {
  if (!slotName) return '';
  const price = fmtAmount(slotPrice, lang);
  return price ? ` · ${slotName} (${price} USD₮)` : ` · ${slotName}`;
}

function slotProgressDesc(pct: number) {
  return (t: TFunction, lang: Lang, _refId: string | null, meta?: NotifDetails | null) => {
    if (meta?.slot && meta?.price) {
      const total = fmtAmount(meta.total, lang);
      const max = fmtAmount(meta.max, lang);
      if (total && max) {
        return t('notifications.kind.slot_progress.descFull', {
          slot: meta.slot,
          price: fmtAmount(meta.price, lang),
          total,
          max,
        });
      }
      return t('notifications.kind.slot_progress.descSlot', {
        slot: meta.slot,
        price: fmtAmount(meta.price, lang),
        pct,
      });
    }
    return t('notifications.kind.slot_progress.descDefault', { pct });
  };
}

const KIND_REGISTRY: Record<string, KindMeta> = {
  claim_ready: {
    labelKey: 'notifications.kind.claim_ready.label',
    description: (t, lang, _r, meta) => {
      const amt = fmtAmount(meta?.amount, lang);
      const n = meta?.count ?? null;
      if (amt && n) {
        const noun =
          lang === 'ru'
            ? t(n === 1 ? 'notifications.kind.claim_ready.nodeOne' : 'notifications.kind.claim_ready.nodeMany')
            : t(n === 1 ? 'notifications.kind.claim_ready.nodeOne' : 'notifications.kind.claim_ready.nodeMany');
        return t('notifications.kind.claim_ready.descAmountN', { amount: amt, count: n, noun });
      }
      if (amt) return t('notifications.kind.claim_ready.descAmount', { amount: amt });
      return t('notifications.kind.claim_ready.descDefault');
    },
    icon: CircleDollarSign,
    tone: 'positive',
  },
  deposit_credited: {
    labelKey: 'notifications.kind.deposit_credited.label',
    description: (t, lang, _r, meta) => {
      const amt = fmtAmount(meta?.amount, lang);
      const net = typeof meta?.network === 'string' ? meta.network : null;
      if (amt && net) return t('notifications.kind.deposit_credited.descAmountNet', { amount: amt, net });
      if (amt) return t('notifications.kind.deposit_credited.descAmount', { amount: amt });
      if (net) return t('notifications.kind.deposit_credited.descNet', { net });
      return t('notifications.kind.deposit_credited.descDefault');
    },
    icon: ArrowDownCircle,
    tone: 'positive',
  },
  slot_purchased: {
    labelKey: 'notifications.kind.slot_purchased.label',
    description: (t, lang, _r, meta) => {
      if (meta?.slot && meta?.price) {
        return t('notifications.kind.slot_purchased.descSlotPrice', {
          slot: meta.slot,
          price: fmtAmount(meta.price, lang),
        });
      }
      if (meta?.slot) return t('notifications.kind.slot_purchased.descSlot', { slot: meta.slot });
      return t('notifications.kind.slot_purchased.descDefault');
    },
    icon: Rocket,
    tone: 'plasma',
  },
  referral_bonus: {
    labelKey: 'notifications.kind.referral_bonus.label',
    description: (t, lang, _r, meta) => {
      const amt = fmtAmount(meta?.amount, lang);
      const who = partnerTag(meta?.partner);
      const lvl = meta?.level ? `L${meta.level}` : '';
      const slotTag = buildSlotTag(meta?.slotName, meta?.slotPrice, lang);
      if (amt && who && lvl) {
        return t('notifications.kind.referral_bonus.descFull', { amount: amt, partner: who, lvl, slotTag });
      }
      if (amt && lvl) return t('notifications.kind.referral_bonus.descAmountLvl', { amount: amt, lvl, slotTag });
      if (amt) return t('notifications.kind.referral_bonus.descAmount', { amount: amt, slotTag });
      return t('notifications.kind.referral_bonus.descDefault');
    },
    icon: Users,
    tone: 'positive',
  },
  referral_income: {
    labelKey: 'notifications.kind.referral_income.label',
    description: (t, lang, _r, meta) => {
      const amt = fmtAmount(meta?.amount, lang);
      const n = meta?.fromCount ?? null;
      if (amt && n) return t('notifications.kind.referral_income.descFromN', { amount: amt, count: n });
      if (amt) return t('notifications.kind.referral_income.descAmount', { amount: amt });
      return t('notifications.kind.referral_income.descDefault');
    },
    icon: TrendingUp,
    tone: 'positive',
  },
  daily_earnings_digest: {
    labelKey: 'notifications.kind.daily_earnings_digest.label',
    description: (t, lang, _r, meta) => {
      const one = Number(meta?.oneTime ?? 0);
      const daily = Number(meta?.daily ?? 0);
      const total = fmtAmount(String(one + daily), lang);
      if (one > 0 && daily > 0) {
        return t('notifications.kind.daily_earnings_digest.descBoth', {
          total,
          one: fmtAmount(meta?.oneTime, lang),
          daily: fmtAmount(meta?.daily, lang),
        });
      }
      if (one > 0) return t('notifications.kind.daily_earnings_digest.descOneTime', { one: fmtAmount(meta?.oneTime, lang) });
      if (daily > 0) return t('notifications.kind.daily_earnings_digest.descDaily', { daily: fmtAmount(meta?.daily, lang) });
      return t('notifications.kind.daily_earnings_digest.descDefault');
    },
    icon: CircleDollarSign,
    tone: 'positive',
  },
  fomo_daily_digest: {
    labelKey: 'notifications.kind.fomo_daily_digest.label',
    description: (t, lang, _r, meta) => {
      const ln = Number(meta?.lostNoActive ?? 0);
      const lg = Number(meta?.lostGate ?? 0);
      const total = fmtAmount(String(ln + lg), lang);
      if (ln > 0 && lg > 0) return t('notifications.kind.fomo_daily_digest.descBoth', { total });
      if (ln > 0) return t('notifications.kind.fomo_daily_digest.descNoActive', { amount: fmtAmount(meta?.lostNoActive, lang) });
      if (lg > 0) return t('notifications.kind.fomo_daily_digest.descGate', { amount: fmtAmount(meta?.lostGate, lang) });
      return t('notifications.kind.fomo_daily_digest.descDefault');
    },
    icon: AlertTriangle,
    tone: 'negative',
  },
  new_partner_joined: {
    labelKey: 'notifications.kind.new_partner_joined.label',
    description: (t, _lang, _r, meta) => {
      const who = partnerTag(meta?.partner);
      const lvl = meta?.level ? `L${meta.level}` : '';
      if (who && lvl) return t('notifications.kind.new_partner_joined.descWhoLvl', { partner: who, lvl });
      if (who) return t('notifications.kind.new_partner_joined.descWho', { partner: who });
      return t('notifications.kind.new_partner_joined.descDefault');
    },
    icon: UserPlus,
    tone: 'positive',
  },
  status_achieved: {
    labelKey: 'notifications.kind.status_achieved.label',
    description: (t, lang, _r, meta) => {
      const b = fmtAmount(meta?.bonus, lang);
      if (meta?.status && b) return t('notifications.kind.status_achieved.descStatusBonus', { status: meta.status, bonus: b });
      if (meta?.status) return t('notifications.kind.status_achieved.descStatus', { status: meta.status });
      return t('notifications.kind.status_achieved.descDefault');
    },
    icon: Trophy,
    tone: 'lunar',
  },
  rank_achieved: {
    labelKey: 'notifications.kind.rank_achieved.label',
    description: (t, lang, _r, meta) => {
      const b = fmtAmount(meta?.bonus, lang);
      if (meta?.rank && b) return t('notifications.kind.rank_achieved.descRankBonus', { rank: meta.rank, bonus: b });
      if (meta?.rank) return t('notifications.kind.rank_achieved.descRank', { rank: meta.rank });
      return t('notifications.kind.rank_achieved.descDefault');
    },
    icon: Award,
    tone: 'lunar',
  },
  plasma_core_expiring: {
    labelKey: 'notifications.kind.plasma_core_expiring.label',
    description: (t, _lang, _r, meta) => {
      const n = meta?.count ?? null;
      if (n && n > 1) return t('notifications.kind.plasma_core_expiring.descN', { count: n });
      return t('notifications.kind.plasma_core_expiring.descDefault');
    },
    icon: Flame,
    tone: 'warning',
  },
  plasma_core_claim: {
    labelKey: 'notifications.kind.plasma_core_claim.label',
    description: (t, lang, _r, meta) => {
      const amt = fmtAmount(meta?.amount, lang);
      const n = meta?.count ?? null;
      if (amt && n && n > 1) return t('notifications.kind.plasma_core_claim.descAmountN', { amount: amt, count: n });
      if (amt) return t('notifications.kind.plasma_core_claim.descAmount', { amount: amt });
      return t('notifications.kind.plasma_core_claim.descDefault');
    },
    icon: Zap,
    tone: 'plasma',
  },
  slot_progress_80: { labelKey: 'notifications.kind.slot_progress.label', description: slotProgressDesc(80), icon: BarChart3, tone: 'warning' },
  slot_progress_85: { labelKey: 'notifications.kind.slot_progress.label', description: slotProgressDesc(85), icon: BarChart3, tone: 'warning' },
  slot_progress_90: { labelKey: 'notifications.kind.slot_progress.label', description: slotProgressDesc(90), icon: BarChart3, tone: 'warning' },
  slot_progress_95: { labelKey: 'notifications.kind.slot_progress.label', description: slotProgressDesc(95), icon: BarChart3, tone: 'warning' },
  quick_start_progress: {
    labelKey: 'notifications.kind.quick_start_progress.label',
    description: (t, _lang, _r, meta) => {
      const cur = meta?.current ?? null;
      const days = meta?.daysLeft ?? null;
      if (cur !== null && days !== null) return t('notifications.kind.quick_start_progress.descCurDays', { cur, days });
      return t('notifications.kind.quick_start_progress.descDefault');
    },
    icon: Zap,
    tone: 'lunar',
  },
  quick_start_ready_to_claim: {
    labelKey: 'notifications.kind.quick_start_ready_to_claim.label',
    description: (t) => t('notifications.kind.quick_start_ready_to_claim.desc'),
    icon: Gift,
    tone: 'lunar',
  },
  quick_start_bonus: {
    labelKey: 'notifications.kind.quick_start_bonus.label',
    description: (t, lang, _r, meta) => {
      const amt = fmtAmount(meta?.amount, lang);
      if (amt) return t('notifications.kind.quick_start_bonus.descAmount', { amount: amt });
      return t('notifications.kind.quick_start_bonus.descDefault');
    },
    icon: Gift,
    tone: 'positive',
  },
  early_bird_low: {
    labelKey: 'notifications.kind.early_bird_low.label',
    description: (t, lang, _r, meta) => {
      const rem = fmtAmount(meta?.remaining, lang);
      const init = fmtAmount(meta?.initial, lang);
      if (rem && init) return t('notifications.kind.early_bird_low.descRem', { rem, init });
      return t('notifications.kind.early_bird_low.descDefault');
    },
    icon: AlertTriangle,
    tone: 'warning',
  },
  fomo: {
    labelKey: 'notifications.kind.fomo.label',
    description: (t) => t('notifications.kind.fomo.desc'),
    icon: AlertTriangle,
    tone: 'negative',
  },
  withdrawal_processed: {
    labelKey: 'notifications.kind.withdrawal_processed.label',
    description: (t) => t('notifications.kind.withdrawal_processed.desc'),
    icon: ArrowUpCircle,
    tone: 'positive',
  },
  withdrawal_failed: {
    labelKey: 'notifications.kind.withdrawal_failed.label',
    description: (t) => t('notifications.kind.withdrawal_failed.desc'),
    icon: AlertTriangle,
    tone: 'negative',
  },
  p2p_received: {
    labelKey: 'notifications.kind.p2p_received.label',
    description: (t, lang, _r, meta) => {
      const amt = fmtAmount(meta?.amount, lang);
      const from = meta?.counterparty
        ? partnerTag(meta.counterparty)
        : t('notifications.partnerSenderFallback');
      if (amt) return t('notifications.kind.p2p_received.descAmount', { amount: amt, from });
      return t('notifications.kind.p2p_received.descDefault', { from });
    },
    icon: ArrowDownCircle,
    tone: 'positive',
  },
  p2p_sent: {
    labelKey: 'notifications.kind.p2p_sent.label',
    description: (t, lang, _r, meta) => {
      const amt = fmtAmount(meta?.amount, lang);
      const to = meta?.counterparty
        ? partnerTag(meta.counterparty)
        : t('notifications.partnerFallback');
      if (amt) return t('notifications.kind.p2p_sent.descAmount', { amount: amt, to });
      return t('notifications.kind.p2p_sent.descDefault', { to });
    },
    icon: ArrowUpCircle,
    tone: 'lunar',
  },
  welcome: {
    labelKey: 'notifications.kind.welcome.label',
    description: (t) => t('notifications.kind.welcome.desc'),
    icon: Sparkles,
    tone: 'lunar',
  },
  onboarding_bonus: {
    labelKey: 'notifications.kind.onboarding_bonus.label',
    description: (t, lang, _r, meta) => {
      const amt = fmtAmount(meta?.amount, lang);
      if (amt) return t('notifications.kind.onboarding_bonus.descAmount', { amount: amt });
      return t('notifications.kind.onboarding_bonus.descDefault');
    },
    icon: Gift,
    tone: 'positive',
  },
  partner_status: {
    labelKey: 'notifications.kind.partner_status.label',
    description: (t, _lang, _r, meta) => {
      const who = partnerTag(meta?.partner);
      const lvl = meta?.level ? `L${meta.level}` : '';
      const chain = meta?.chain ? ` · ${meta.chain}` : '';
      if (who && meta?.status && lvl) return t('notifications.kind.partner_status.descFull', { partner: who, lvl, status: meta.status, chain });
      if (meta?.status) return t('notifications.kind.partner_status.descStatus', { status: meta.status, chain });
      return t('notifications.kind.partner_status.descDefault');
    },
    icon: Trophy,
    tone: 'lunar',
  },
  partner_rank: {
    labelKey: 'notifications.kind.partner_rank.label',
    description: (t, _lang, _r, meta) => {
      const who = partnerTag(meta?.partner);
      const lvl = meta?.level ? `L${meta.level}` : '';
      const chain = meta?.chain ? ` · ${meta.chain}` : '';
      if (who && meta?.rank && lvl) return t('notifications.kind.partner_rank.descFull', { partner: who, lvl, rank: meta.rank, chain });
      if (meta?.rank) return t('notifications.kind.partner_rank.descRank', { rank: meta.rank, chain });
      return t('notifications.kind.partner_rank.descDefault');
    },
    icon: Award,
    tone: 'lunar',
  },
  fomo_gate_closed: {
    labelKey: 'notifications.kind.fomo_gate_closed.label',
    description: (t, lang, _r, meta) => {
      const lost = fmtAmount(meta?.lost, lang);
      const lvl = meta?.level ?? null;
      const need = meta?.requiredRefs ?? null;
      const partner = partnerTag(meta?.partner);
      const whoTag = partner ? ` · ${partner}` : '';
      const slotTag = buildSlotTag(meta?.slotName, meta?.slotPrice, lang);
      if (lost && lvl && need) return t('notifications.kind.fomo_gate_closed.descFull', { lost, lvl, who: whoTag, slot: slotTag, need });
      if (lost && lvl) return t('notifications.kind.fomo_gate_closed.descLostLvl', { lost, lvl, who: whoTag, slot: slotTag });
      if (lost) return t('notifications.kind.fomo_gate_closed.descLost', { lost, who: whoTag, slot: slotTag });
      return t('notifications.kind.fomo_gate_closed.descDefault');
    },
    icon: AlertTriangle,
    tone: 'negative',
  },
  fomo_no_active_contract: {
    labelKey: 'notifications.kind.fomo_no_active_contract.label',
    description: (t, lang, _r, meta) => {
      const lost = fmtAmount(meta?.lost, lang);
      const lvl = meta?.level ?? null;
      const partner = partnerTag(meta?.partner);
      const whoTag = partner ? ` · ${partner}` : '';
      const slotTag = buildSlotTag(meta?.slotName, meta?.slotPrice, lang);
      if (lost && lvl) return t('notifications.kind.fomo_no_active_contract.descLostLvl', { lost, lvl, who: whoTag, slot: slotTag });
      if (lost) return t('notifications.kind.fomo_no_active_contract.descLost', { lost, who: whoTag, slot: slotTag });
      return t('notifications.kind.fomo_no_active_contract.descDefault');
    },
    icon: AlertTriangle,
    tone: 'negative',
  },
  fomo_overflow_capacity: {
    labelKey: 'notifications.kind.fomo_overflow_capacity.label',
    description: (t, lang, _r, meta) => {
      const lost = fmtAmount(meta?.lost, lang);
      const slotTag = buildSlotTag(meta?.slotName, meta?.slotPrice, lang);
      if (lost) return t('notifications.kind.fomo_overflow_capacity.descLost', { lost, slot: slotTag });
      return t('notifications.kind.fomo_overflow_capacity.descDefault');
    },
    icon: AlertTriangle,
    tone: 'warning',
  },
  plasma_core_expiring_24h: {
    labelKey: 'notifications.kind.plasma_core_expiring_24h.label',
    description: (t, _lang, _r, meta) => {
      const n = meta?.count ?? null;
      if (n && n > 1) return t('notifications.kind.plasma_core_expiring_24h.descN', { count: n });
      return t('notifications.kind.plasma_core_expiring_24h.descDefault');
    },
    icon: Clock,
    tone: 'warning',
  },
  plasma_core_expired: {
    labelKey: 'notifications.kind.plasma_core_expired.label',
    description: (t, _lang, _r, meta) => {
      const n = meta?.count ?? null;
      if (n && n > 1) return t('notifications.kind.plasma_core_expired.descN', { count: n });
      return t('notifications.kind.plasma_core_expired.descDefault');
    },
    icon: Flame,
    tone: 'negative',
  },
  manual_claim: {
    labelKey: 'notifications.kind.manual_claim.label',
    description: (t, lang, _r, meta) => {
      const amt = fmtAmount(meta?.amount, lang);
      if (amt) return t('notifications.kind.manual_claim.descAmount', { amount: amt });
      return t('notifications.kind.manual_claim.descDefault');
    },
    icon: Sparkles,
    tone: 'positive',
  },
  auto_claim: {
    labelKey: 'notifications.kind.auto_claim.label',
    description: (t, lang, _r, meta) => {
      const amt = fmtAmount(meta?.amount, lang);
      if (amt) return t('notifications.kind.auto_claim.descAmount', { amount: amt });
      return t('notifications.kind.auto_claim.descDefault');
    },
    icon: Zap,
    tone: 'positive',
  },
  plasma_core_recharge_all: {
    labelKey: 'notifications.kind.plasma_core_recharge_all.label',
    description: (t, lang, _r, meta) => {
      const n = meta?.count ?? null;
      const cost = fmtAmount(meta?.cost, lang);
      if (n && cost) return t('notifications.kind.plasma_core_recharge_all.descFull', { count: n, cost });
      if (n) return t('notifications.kind.plasma_core_recharge_all.descCount', { count: n });
      return t('notifications.kind.plasma_core_recharge_all.descDefault');
    },
    icon: Zap,
    tone: 'plasma',
  },
  slot_burned: {
    labelKey: 'notifications.kind.slot_burned.label',
    description: (t, lang, _r, meta) => {
      const total = fmtAmount(meta?.total, lang);
      if (meta?.slot && meta?.price && total) return t('notifications.kind.slot_burned.descFull', { slot: meta.slot, price: fmtAmount(meta.price, lang), total });
      if (meta?.slot) return t('notifications.kind.slot_burned.descSlot', { slot: meta.slot });
      return t('notifications.kind.slot_burned.descDefault');
    },
    icon: Flame,
    tone: 'negative',
  },
  line_unlocked: {
    labelKey: 'notifications.kind.line_unlocked.label',
    description: (t, _lang, refId, meta) => {
      const lineNum = meta?.line ?? (refId?.match(/line:(\d+)/)?.[1] ?? null);
      const pct = meta?.lineBonusPct ?? meta?.bonusPct ?? null;
      if (lineNum && pct) return t('notifications.kind.line_unlocked.descFull', { line: lineNum, pct });
      if (lineNum) return t('notifications.kind.line_unlocked.descLine', { line: lineNum });
      return t('notifications.kind.line_unlocked.descDefault');
    },
    icon: Gem,
    tone: 'positive',
  },
  new_slots_available: {
    labelKey: 'notifications.kind.new_slots_available.label',
    description: (t, lang, refId, meta) => {
      const month = meta?.month ?? (refId?.match(/month:(\d+)/)?.[1] ?? null);
      const list = meta?.slots?.map((s) => `${s.name} (${fmtAmount(s.price, lang)})`).join(', ') ?? '';
      if (month && list) return t('notifications.kind.new_slots_available.descFull', { month, list });
      if (month) return t('notifications.kind.new_slots_available.descMonth', { month });
      return t('notifications.kind.new_slots_available.descDefault');
    },
    icon: Package,
    tone: 'lunar',
  },
  rank_expiring: {
    labelKey: 'notifications.kind.rank_expiring.label',
    description: (t, lang, _r, meta) => {
      const m = fmtAmount(meta?.missing, lang);
      const days = meta?.days ?? 5;
      if (meta?.rank && m) return t('notifications.kind.rank_expiring.descFull', { rank: meta.rank, missing: m, days });
      if (meta?.rank) return t('notifications.kind.rank_expiring.descRank', { rank: meta.rank, days });
      return t('notifications.kind.rank_expiring.descDefault', { days });
    },
    icon: Clock,
    tone: 'warning',
  },
  access_approved: {
    labelKey: 'notifications.kind.access_approved.label',
    description: (t) => t('notifications.kind.access_approved.desc'),
    icon: CheckCircle,
    tone: 'positive',
  },
  access_blocked: {
    labelKey: 'notifications.kind.access_blocked.label',
    description: (t) => t('notifications.kind.access_blocked.desc'),
    icon: XCircle,
    tone: 'negative',
  },
  access_requested: {
    labelKey: 'notifications.kind.access_requested.label',
    description: (t) => t('notifications.kind.access_requested.desc'),
    icon: UserPlus,
    tone: 'warning',
  },
};

const DEFAULT_META: KindMeta = {
  labelKey: 'notifications.kind.system.label',
  description: (t) => t('notifications.kind.system.desc'),
  icon: Bell,
  tone: 'lunar',
};

// ────────────────────────────────────────────────────────────────────────────
// Wire types — mirror the backend /api/luna/notifications response
// ────────────────────────────────────────────────────────────────────────────
interface ApiNotification {
  id: string;
  kind: string;
  referenceId: string | null;
  sentAt: string;
  read: boolean;
  meta?: NotifDetails | null;
}

export type NotifMeta = ApiNotification['meta'];

interface ApiResponse {
  success: boolean;
  items: ApiNotification[];
  unreadCount: number;
}

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────
export function NotificationBell() {
  const { haptic } = useTelegramWebApp();
  const uiSounds = useUISounds();
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.language === 'ru' ? 'ru' : 'en';
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<ApiNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<number | null>(null);

  // ── Fetch the list ───────────────────────────────────────────────
  const refresh = useCallback(async (): Promise<boolean> => {
    // Без токена запрос бессмыслен — вернётся 401 в консоль.
    if (!getAuthToken()) return false;

    setLoading(true);
    setError(null);
    try {
      const res = await lunaApi.notifications.list();
      const data = res.data as ApiResponse;
      if (data.success) {
        setItems(data.items);
        setUnreadCount(data.unreadCount);
        return true;
      }
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status && status !== 401) {
        setError(t('notifications.loadError'));
      }
    } finally {
      setLoading(false);
    }
    return false;
  }, [t]);

  // Первая загрузка + lightweight poll каждые 60 с (раньше 30).
  // Если JWT ещё не готов (холодный старт), повторяем каждые 1500 мс
  // с лимитом 8 попыток (12 сек всего) — чтобы не бомбить бэк при
  // ошибке авторизации и не ловить 429.
  useEffect(() => {
    let retryId: number | null = null;
    let retriesLeft = 8;
    const tryNow = async () => {
      const ok = await refresh();
      if (ok || --retriesLeft <= 0) {
        if (retryId !== null) {
          window.clearInterval(retryId);
          retryId = null;
        }
      }
    };
    void tryNow();
    retryId = window.setInterval(() => { void tryNow(); }, 1500);

    pollingRef.current = window.setInterval(() => {
      void refresh();
    }, 60_000);

    return () => {
      if (retryId !== null) window.clearInterval(retryId);
      if (pollingRef.current !== null) {
        window.clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [refresh]);

  // ── Open / close ─────────────────────────────────────────────────
  const handleToggle = useCallback(() => {
    haptic.light();
    // Звук ВНЕ setState-callback: iOS требует play() в том же
    // user-gesture что и сам клик. setState((prev)=>...) колбэк
    // выполняется React-ом в другом microtask и теряет activation.
    if (open) uiSounds.bellClose();
    else uiSounds.bellOpen();
    setOpen((prev) => !prev);
    // Закрываем welcome-toast если он перекрывает колокольчик —
    // юзеру важнее прочитать уведомления сразу.
    window.dispatchEvent(new CustomEvent('luna:bell-open'));
  }, [haptic, open, uiSounds]);

  // When the dropdown opens we mark everything read so the user stops
  // seeing the badge. The backend call is idempotent.
  useEffect(() => {
    if (!open || unreadCount === 0) return;
    void lunaApi.notifications
      .markRead()
      .then(() => {
        setItems((prev) => prev.map((i) => ({ ...i, read: true })));
        setUnreadCount(0);
      })
      .catch(() => {
        // Silently swallow — not critical.
      });
  }, [open, unreadCount]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Close when clicking anywhere outside the dropdown
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest?.('[data-notification-bell]')) return;
      setOpen(false);
    };
    // Use a microtask so the click that OPENED the bell doesn't
    // immediately close it.
    const id = window.setTimeout(() => {
      window.addEventListener('click', onClick);
    }, 0);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('click', onClick);
    };
  }, [open]);

  const displayItems = useMemo(() => items.slice(0, 30), [items]);

  return (
    <div className="relative" data-notification-bell>
      {/* ── Bell button ── */}
      <button
        type="button"
        aria-label={t('notifications.bellAria')}
        onClick={handleToggle}
        data-no-sound="true"
        className={cn(
          'lg-pill h-9 w-9 flex items-center justify-center',
          'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
          'transition-all duration-300 active:scale-90',
        )}
      >
        <Bell size={16} strokeWidth={1.8} />
      </button>
      {/* Badge — SIBLING of button (иначе .lg-pill { contain: paint } клипает) */}
      {unreadCount > 0 && (
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
          className="pointer-events-none absolute top-[-4px] right-[-4px] min-w-[16px] h-[16px] rounded-full px-1 flex items-center justify-center text-[9px] font-extrabold text-white z-20"
          style={{
            background:
              'linear-gradient(180deg, #fb7185 0%, #f43f5e 100%)',
            boxShadow:
              '0 0 0 1.5px var(--bg-base), 0 4px 10px -2px rgba(244, 63, 94, 0.6)',
          }}
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </motion.span>
      )}

      {/* ── Dropdown sheet ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, x: 360 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 360 }}
            transition={{ type: 'spring', stiffness: 280, damping: 30, mass: 0.9 }}
            className="absolute right-0 top-[46px] w-[320px] max-w-[calc(100vw-32px)] z-40"
            style={{
              maxHeight: 'calc(100dvh - 120px)',
            }}
          >
            <div
              className="rounded-[24px] overflow-hidden flex flex-col"
              style={{
                // Плотный фон без backdrop-filter — мобильный WebView
                // рендерит blur с задержкой → визуальный "скачок"
                background:
                  'linear-gradient(180deg, rgba(18, 18, 30, 0.98) 0%, rgba(12, 12, 22, 0.99) 100%)',
                border: '1px solid var(--glass-stroke-strong)',
                boxShadow:
                  '0 24px 56px -12px rgba(0,0,0,0.7), inset 0 1.5px 0 0 var(--glass-rim-light), inset 0 -1px 0 0 var(--glass-rim-dark)',
                maxHeight: 'inherit',
              }}
            >
              {/* Header */}
              <div className="px-4 py-3 flex items-center justify-between border-b border-[var(--glass-stroke)]">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">
                    {t('notifications.centerKicker')}
                  </p>
                  <h3 className="font-display text-[15px] font-extrabold text-[var(--text-primary)] tracking-tight">
                    {t('notifications.centerTitle')}
                  </h3>
                </div>
                {displayItems.length > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      haptic.medium();
                      uiSounds.bellClear();
                      void lunaApi.notifications.clearAll()
                        .then(() => {
                          setItems([]);
                          setUnreadCount(0);
                        })
                        .catch(() => { /* silent */ });
                    }}
                    data-no-sound="true"
                    className="px-2.5 py-1 rounded-full text-[10px] font-mono font-bold uppercase tracking-[0.1em] transition active:scale-95"
                    style={{
                      background:
                        'color-mix(in oklab, var(--status-negative) 14%, transparent)',
                      color: 'var(--status-negative)',
                      border:
                        '1px solid color-mix(in oklab, var(--status-negative) 30%, transparent)',
                    }}
                  >
                    {t('notifications.clearAll')}
                  </button>
                )}
              </div>

              {/* Body — scrollable list */}
              <div
                className="overflow-y-auto overscroll-contain"
                style={{
                  maxHeight: 'calc(100dvh - 220px)',
                }}
              >
                {loading && displayItems.length === 0 && (
                  <div className="px-4 py-8 text-center text-[12px] text-[var(--text-muted)]">
                    {t('notifications.loading')}
                  </div>
                )}
                {error && (
                  <div className="px-4 py-6 text-center text-[12px] text-[var(--status-negative)]">
                    {error}
                  </div>
                )}
                {!loading && !error && displayItems.length === 0 && (
                  <div className="px-4 py-10 text-center">
                    <div className="mx-auto mb-3 h-12 w-12 rounded-full lg-pill flex items-center justify-center">
                      <Bell
                        size={20}
                        strokeWidth={1.6}
                        className="text-[var(--text-muted)]"
                      />
                    </div>
                    <p className="text-[13px] text-[var(--text-secondary)] font-semibold">
                      {t('notifications.emptyTitle')}
                    </p>
                    <p className="text-[11px] text-[var(--text-muted)] mt-1">
                      {t('notifications.emptySubtitle')}
                    </p>
                  </div>
                )}
                <ul>
                  {displayItems.map((n) => (
                    <NotificationItem key={n.id} notification={n} t={t} lang={lang} />
                  ))}
                </ul>
              </div>

              {/* Footer hint */}
              {displayItems.length > 0 && (
                <div className="px-4 py-2.5 border-t border-[var(--glass-stroke)] flex items-center justify-between text-[10px] text-[var(--text-muted)]">
                  <span>{t('notifications.totalHint', { count: items.length })}</span>
                  <span>{t('notifications.tapHint')}</span>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Individual row — keeps the list virtualization-friendly if we ever
// need to scale it up later.
// ────────────────────────────────────────────────────────────────────────────
function NotificationItem({
  notification,
  t,
  lang,
}: {
  notification: ApiNotification;
  t: TFunction;
  lang: Lang;
}) {
  const meta = KIND_REGISTRY[notification.kind] ?? DEFAULT_META;
  const Icon = meta.icon;
  const ts = useMemo(
    () => formatRelative(notification.sentAt, t, lang),
    [notification.sentAt, t, lang],
  );

  // slot_progress_* → kind.label принимает {{pct}}
  const labelParams: Record<string, unknown> = {};
  const pctMatch = notification.kind.match(/^slot_progress_(\d+)$/);
  if (pctMatch) labelParams.pct = Number(pctMatch[1]);

  return (
    <li
      className={cn(
        'flex items-start gap-3 px-4 py-3 border-b border-[var(--glass-stroke)] last:border-0',
        'transition-colors duration-200',
        !notification.read && 'bg-white/[0.03]',
      )}
    >
      <div
        className="h-9 w-9 rounded-2xl flex items-center justify-center flex-shrink-0"
        style={{
          background: `color-mix(in oklab, ${toneToColor(meta.tone)} 16%, transparent)`,
          boxShadow: `inset 0 0 0 1px color-mix(in oklab, ${toneToColor(meta.tone)} 32%, transparent)`,
        }}
      >
        <Icon size={16} strokeWidth={1.8} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[13px] font-semibold text-[var(--text-primary)] truncate">
            {t(meta.labelKey, labelParams)}
          </p>
          <span className="text-[10px] text-[var(--text-muted)] flex-shrink-0">
            {ts}
          </span>
        </div>
        <p className="text-[11px] text-[var(--text-secondary)] leading-snug mt-0.5">
          {meta.description(t, lang, notification.referenceId, notification.meta)}
        </p>
      </div>
      {!notification.read && (
        <div
          aria-hidden
          className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5"
          style={{
            background: 'var(--accent-primary)',
            boxShadow: '0 0 10px 0 var(--accent-primary)',
          }}
        />
      )}
    </li>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────
function toneToColor(tone: Tone): string {
  switch (tone) {
    case 'lunar':
      return 'var(--accent-primary)';
    case 'plasma':
      return 'var(--accent-secondary)';
    case 'nebula':
      return 'var(--accent-tertiary)';
    case 'positive':
      return 'var(--status-positive)';
    case 'warning':
      return 'var(--status-warning)';
    case 'negative':
      return 'var(--status-negative)';
  }
}

function formatRelative(iso: string, t: TFunction, lang: Lang): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return t('notifications.relative.now');
  if (mins < 60) return t('notifications.relative.min', { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t('notifications.relative.hour', { count: hrs });
  const days = Math.floor(hrs / 24);
  if (days < 7) return t('notifications.relative.day', { count: days });
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return lang === 'ru' ? `${dd}.${mm}` : `${mm}/${dd}`;
}
